import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import api from '../services/api'

const AuthContext = createContext()
export const useAuth = () => useContext(AuthContext)
const TIMEOUT_MS = 30 * 60 * 1000
const WARNING_MS = 5 * 60 * 1000
const RENEW_MS = 5 * 60 * 1000
const PUBLIC_PATHS = ['/sign/', '/verify/', '/login', '/register']

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [sessionError, setSessionError] = useState('')
  const [retry, setRetry] = useState(0)
  const userRef = useRef(null)
  const lastActivityRef = useRef(Date.now())
  const lastRenewRef = useRef(0)
  const renewalRef = useRef(null)
  const generationRef = useRef(0)
  const warnedRef = useRef(false)
  const saveUser = useCallback(value => { userRef.current = value; setUser(value) }, [])

  const logout = useCallback((notifyServer = true) => {
    generationRef.current += 1
    if (notifyServer) api.post('/auth/logout').catch(() => {})
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('lastActivity')
    saveUser(null)
    setSessionError('')
  }, [saveUser])

  const markActivity = useCallback(() => {
    if (!userRef.current) return
    const now = Date.now()
    // Avoid writing localStorage on every mouse movement.
    if (now - lastActivityRef.current >= 1000) {
      lastActivityRef.current = now
      localStorage.setItem('lastActivity', String(now))
    }
    warnedRef.current = false
  }, [])

  const renewSession = useCallback(() => {
    if (renewalRef.current) return renewalRef.current
    const generation = generationRef.current
    const pending = api.get('/auth/me', { params: { renew: '1' }, timeout: 15000 })
      .then(res => {
        if (generation !== generationRef.current || !userRef.current) return false
        if (!res.data?.success || !res.data?.user) throw new Error('Invalid session response')
        lastRenewRef.current = Date.now()
        setSessionError('')
        // /auth/me in the existing controller does not return division_ids.
        const next = { ...userRef.current, ...res.data.user }
        saveUser(next)
        localStorage.setItem('user', JSON.stringify(next))
        return true
      }).catch(error => {
        if (generation === generationRef.current && error.response?.status !== 401) {
          setSessionError('ตรวจสอบหรือต่ออายุเซสชันไม่ได้ชั่วคราว กรุณาตรวจการเชื่อมต่อแล้วลองอีกครั้ง')
        }
        return false
      }).finally(() => { if (renewalRef.current === pending) renewalRef.current = null })
    renewalRef.current = pending
    return pending
  }, [saveUser])

  useEffect(() => {
    let active = true
    const generation = generationRef.current
    const restore = async () => {
      if (PUBLIC_PATHS.some(path => path.endsWith('/')
        ? window.location.pathname.startsWith(path)
        : window.location.pathname === path || window.location.pathname === path + '/')) {
        if (active) setInitialized(true)
        return
      }
      const savedUser = localStorage.getItem('user')
      const last = Number(localStorage.getItem('lastActivity'))
      if (savedUser && (!Number.isFinite(last) || !last || Date.now() - last >= TIMEOUT_MS)) {
        logout()
        if (active) setInitialized(true)
        return
      }
      try {
        const res = await api.get('/auth/me', { timeout: 15000 })
        if (!active || generation !== generationRef.current) return
        if (!res.data?.success || !res.data?.user) throw new Error('Invalid session response')
        let previous = {}
        try { previous = JSON.parse(savedUser || '{}') } catch (_) {}
        const next = { ...previous, ...res.data.user }
        saveUser(next)
        localStorage.setItem('user', JSON.stringify(next))
        lastActivityRef.current = savedUser && last > 0 ? last : Date.now()
        localStorage.setItem('lastActivity', String(lastActivityRef.current))
        lastRenewRef.current = 0
        setSessionError('')
      } catch (error) {
        if (!active || generation !== generationRef.current) return
        if (error.response?.status === 401) logout(false)
        else setSessionError('ระบบตรวจสอบเซสชันขัดข้องชั่วคราว ข้อมูลเข้าสู่ระบบยังไม่ถูกล้าง กรุณาลองอีกครั้ง')
      } finally { if (active) setInitialized(true) }
    }
    restore()
    return () => { active = false }
  }, [logout, saveUser, retry])

  useEffect(() => {
    const ended = () => logout(false)
    const storage = event => {
      if (event.key === 'user' && event.newValue === null) logout(false)
      if (event.key === 'lastActivity' && event.newValue && userRef.current) {
        const value = Number(event.newValue)
        if (Number.isFinite(value) && value > lastActivityRef.current && value <= Date.now()) {
          lastActivityRef.current = value
          warnedRef.current = false
        }
      }
    }
    window.addEventListener('staff-session-ended', ended)
    window.addEventListener('storage', storage)
    return () => {
      window.removeEventListener('staff-session-ended', ended)
      window.removeEventListener('storage', storage)
    }
  }, [logout])

  useEffect(() => {
    if (!user) return
    const expire = () => {
      logout()
      window.location.assign('/login')
    }
    const activity = () => {
      if (Date.now() - lastActivityRef.current >= TIMEOUT_MS) { expire(); return }
      markActivity()
    }
    const check = async () => {
      if (!userRef.current) return
      const idle = Date.now() - lastActivityRef.current
      if (idle >= TIMEOUT_MS) { expire(); return }
      if (document.visibilityState === 'hidden') return
      if (idle >= TIMEOUT_MS - WARNING_MS) {
        if (!warnedRef.current) {
          warnedRef.current = true
          const before = Date.now()
          const extend = window.confirm('ไม่ได้ใช้งานใกล้ครบ 30 นาที กด OK เพื่อต่ออายุเซสชัน')
          // A suspended tab/dialog must not revive an already idle session.
          if (Date.now() - before >= TIMEOUT_MS - idle) { expire(); return }
          if (extend) {
            const renewed = await renewSession()
            if (renewed && userRef.current) markActivity()
          }
        }
        return
      }
      if (Date.now() - lastRenewRef.current >= RENEW_MS) await renewSession()
    }
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(name => window.addEventListener(name, activity, { passive: true }))
    const visible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', visible)
    const interval = setInterval(check, 30000)
    check()
    return () => {
      clearInterval(interval)
      events.forEach(name => window.removeEventListener(name, activity))
      document.removeEventListener('visibilitychange', visible)
    }
  // Use the authenticated user id; renewal must not reinstall activity timers.
  }, [user?.id, logout, markActivity, renewSession])

  const login = async (username, password) => {
    try {
      const res = await api.post('/auth/login', { username, password })
      if (!res.data?.success || !res.data?.user) throw new Error('Invalid login response')
      generationRef.current += 1
      saveUser(res.data.user)
      localStorage.setItem('user', JSON.stringify(res.data.user))
      lastActivityRef.current = Date.now()
      localStorage.setItem('lastActivity', String(lastActivityRef.current))
      lastRenewRef.current = Date.now()
      warnedRef.current = false
      setSessionError('')
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'ไม่สามารถเข้าสู่ระบบได้ กรุณาตรวจการเชื่อมต่อและลองใหม่' }
    }
  }
  const retrySession = () => { setInitialized(false); setRetry(value => value + 1) }
  const value = { user, login, logout, loading: !initialized, sessionError, retrySession }
  return (
    <AuthContext.Provider value={value}>
      {!initialized ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p>กำลังตรวจสอบเซสชัน...</p></div>
      ) : sessionError && !user ? (
        <div role="alert" style={{ padding: '32px', textAlign: 'center' }}>
          <p>{sessionError}</p><button type="button" onClick={retrySession}>ลองตรวจสอบอีกครั้ง</button>
        </div>
      ) : <>
        {sessionError && <div role="alert" style={{ padding: '12px', background: '#fff7ed', color: '#9a3412' }}>
          {sessionError} <button type="button" onClick={() => renewSession()}>ลองอีกครั้ง</button>
        </div>}
        {children}
      </>}
    </AuthContext.Provider>
  )
}
